import { createFileRoute, Link } from '@tanstack/react-router'
import { PaletteIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useWipItems } from '@/api/adjustments'
import type { WipItem } from '@/api/adjustments'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatQty } from '@/components/materials/pool-format'
import { RecordList, RecordListEmpty, RecordListItem } from '@/components/record-list'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

// WIP view (UI-SCREENS I-02, 05 §WIP view): the printed, unassembled parts per Model with the
// Pool in each color slot and the qty at the WIP location, flagged "pre-vN" (printed before a
// breaking version) and "unused part" (its Model is not on the BOM in effect today, #170 Q3).
// Both flags are judged as of today (domain-rules Catalog changes).
//
// Adjust / write off opens I-11 at the WIP location with the part prefilled (04l #140); Owner and
// Staff may both do it.
export const Route = createFileRoute('/w/$slug/stock/wip')({
  component: WipView,
  staticData: { breadcrumb: 'stock.wip.title' },
})

function WipView() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const items = useWipItems(slug)

  return (
    <section className="flex flex-col gap-4">
      <h1 className="font-heading text-xl font-semibold">{t('stock.wip.title')}</h1>

      {items.isPending ? (
        <LoadingSkeleton />
      ) : items.isError || !items.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : items.data.length === 0 ? (
        <RecordListEmpty>{t('stock.wip.empty')}</RecordListEmpty>
      ) : (
        <RecordList>
          {items.data.map((item) => (
            <WipRow key={item.stock_item_id} slug={slug} item={item} />
          ))}
        </RecordList>
      )}
    </section>
  )
}

// One part per row: no detail page, so the title is plain. The meta line names the Pool in each
// color slot; the figure is the qty at the WIP location; Adjust / write off stays on the row.
function WipRow({ slug, item }: { slug: string; item: WipItem }) {
  const { t } = useTranslation()

  return (
    <RecordListItem
      title={`${item.model.product.name} · ${item.model.name}`}
      badges={
        <>
          {item.pre_vn ? <Badge variant="outline" title={t('stock.wip.preVnHint')}>{t('stock.wip.preVn')}</Badge> : null}
          {item.unused_part ? (
            <Badge variant="outline" title={t('stock.wip.unusedPartHint')}>
              {t('stock.wip.unusedPart')}
            </Badge>
          ) : null}
        </>
      }
      meta={item.colors.map((color) => ({
        icon: PaletteIcon,
        label: color.label,
        value: `${color.label}: ${color.pool.label}`,
      }))}
      figure={formatQty(item.qty)}
      caption={t('stock.wip.qty')}
      actions={
        <Button asChild variant="outline" size="sm">
          <Link to="/w/$slug/adjustments/new" params={{ slug }} search={{ location: 'wip', itemId: item.stock_item_id }}>
            {t('stock.wip.adjust')}
          </Link>
        </Button>
      }
    />
  )
}
